import React from 'react';
import { ArrowPathIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';

interface RetentionFooterProps {
    isSubmitting: boolean;
    onProceed: () => void;
    onReject: () => void;
}

export function RetentionFooter({ isSubmitting, onProceed, onReject }: RetentionFooterProps) {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!menuOpen) return;

        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };

        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [menuOpen]);

    const handleReject = () => {
        setMenuOpen(false);
        onReject();
    };

    return (
        <div className="flex-shrink-0 mt-4 pt-4 pb-1 border-t border-gray-200">
            <div className="flex gap-2 items-center">
                <button
                    type="button"
                    onClick={onProceed}
                    disabled={isSubmitting}
                    className="flex-1 h-12 rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-2 bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                    {isSubmitting && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
                    Proceed with retention →
                </button>

                <div ref={menuRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setMenuOpen((o) => !o)}
                        disabled={isSubmitting}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        aria-label="Other actions"
                        className="h-12 w-12 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                    >
                        <EllipsisVerticalIcon className="w-5 h-5" />
                    </button>

                    {menuOpen && (
                        <div
                            role="menu"
                            className="absolute bottom-full mb-2 right-0 z-10 w-64 flex flex-col gap-1.5 p-1.5 bg-white border border-gray-200 rounded-xl shadow-lg"
                        >
                            <button
                                type="button"
                                role="menuitem"
                                onClick={handleReject}
                                disabled={isSubmitting}
                                className="h-10 rounded-lg border-[1.5px] border-yellow-400 bg-yellow-50 text-[#B88716] text-sm font-semibold hover:bg-yellow-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Reject retention
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
